import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignatureCaptureModal } from '../../../components/SignatureCapture';
import type { SignatureInfo } from '../../appointments/fhirNoteMapping';
import {
  completeProviderOnboarding,
  type ProviderOnboardingCompletionFailure,
} from '../../../lib/providerOnboardingService';
import { OnboardingSection } from '../OnboardingSection';
import { useOnboardingStatus } from '../useOnboardingStatus';

export function AttestationSection(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const onboardingStatus = useOnboardingStatus();
  const [saving, setSaving] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signature, setSignature] = useState<SignatureInfo | null>(null);
  const [missingSections, setMissingSections] = useState<ProviderOnboardingCompletionFailure['missingSections']>([]);

  const isUnlocked = onboardingStatus.unlockedSections.attestation;
  const prerequisiteMissingSections = ([
    ['personal-info', 'Personal Information'],
    ['availability', 'Availability'],
    ['profile', 'Profile'],
    ['licenses-education', 'Licenses and Education'],
  ] as const)
    .map(([sectionId, title]) => ({
      sectionId,
      title,
      missingFields: onboardingStatus.sections[sectionId].missingFields.length > 0
        ? onboardingStatus.sections[sectionId].missingFields
        : onboardingStatus.sections[sectionId].complete
          ? []
          : ['This section must be marked complete before attesting.'],
    }))
    .filter((section) => !onboardingStatus.sections[section.sectionId].complete);
  const visibleMissingSections = missingSections.length > 0 ? missingSections : prerequisiteMissingSections;

  const handleSign = (sig: SignatureInfo): void => {
    setSignature(sig);
    setSignatureModalOpen(false);
  };

  const handleSave = async (): Promise<void> => {
    if (!signature || !isUnlocked) {
      return;
    }

    setSaving(true);
    setMissingSections([]);
    try {
      const result = await completeProviderOnboarding(medplum, signature);
      if (!result.ok) {
        setMissingSections(result.missingSections);
        return;
      }

      medplum.invalidateSearches('Practitioner');
      medplum.invalidateSearches('Task');
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingSection
      title="Attestation"
      onSave={handleSave}
      saveLabel="Complete Onboarding"
      isSaving={saving}
      saveDisabled={!signature || !isUnlocked}
    >
      <Stack gap="xl">
        <Text size="sm" c="dimmed">
          By signing below, I attest that all information submitted during onboarding is true and accurate to the best of
          my knowledge.
        </Text>

        {visibleMissingSections.length > 0 && (
          <Box
            p="md"
            style={{
              backgroundColor: 'var(--mantine-color-red-0)',
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--mantine-color-red-3)',
            }}
          >
            <Stack gap="xs">
              <Text size="sm" fw={600} c="red.8">
                Complete the missing onboarding fields below before attesting.
              </Text>
              {visibleMissingSections.map((section) => (
                <div key={section.sectionId}>
                  <Text size="sm" fw={600}>
                    {section.title}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {section.missingFields.join(', ')}
                  </Text>
                </div>
              ))}
            </Stack>
          </Box>
        )}

        {!signature && (
          <Group>
            <Button
              leftSection={<IconSignature size={16} />}
              disabled={!isUnlocked}
              onClick={() => setSignatureModalOpen(true)}
            >
              Sign Attestation
            </Button>
          </Group>
        )}

        {signature && (
          <Box
            p="md"
            style={{
              backgroundColor: 'var(--mantine-color-green-0)',
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--mantine-color-green-3)',
            }}
          >
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Signed by {signature.name}
              </Text>
              {signature.signedAt && (
                <Text size="sm" c="dimmed">
                  {new Date(signature.signedAt).toLocaleString()}
                </Text>
              )}
              {signature.ipAddress && (
                <Text size="sm" c="dimmed">
                  IP: {signature.ipAddress}
                </Text>
              )}
            </Stack>
          </Box>
        )}

        {!signature && isUnlocked && (
          <Text size="sm" c="red">
            Signature is required to complete onboarding.
          </Text>
        )}
      </Stack>

      <SignatureCaptureModal
        opened={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSign={handleSign}
        title="Attestation"
        submitLabel="Sign Attestation"
        allowSaveForFuture={false}
      />
    </OnboardingSection>
  );
}
